import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { inr, inrRange, type BriefEstimateInput, type FilmEstimate, type TierKey } from '../lib/estimate';
import { FILM_LOOK_PRESETS, type NewProjectInput } from '../lib/project';
import type { NewSeriesInput, SeriesStartMode } from '../lib/series';
import { Alert, Button } from '../ui/kit/components/primitives';
import { Modal } from '../ui/kit/components/overlays';
import ProjectSetupFields from '../features/projects/ProjectSetupFields';
import EstimateCards from '../features/projects/EstimateCards';
import MarketingIntake from '../features/studio/marketing/MarketingIntake';
import type { Intake } from '../features/studio/marketing/agencyApi';
import '../ui/styles/components.css';
import '../features/projects/projects.css';

export type ProjectKind = 'film' | 'vfx' | 'marketing' | 'upscale' | 'saas';

interface DialogCopy {
  eyebrow: string;
  title: string;
  blurb: string;
  types: string[];
  ideaLabel: string;
  ideaPlaceholder: string;
  submit: string;
  allowCustomType: boolean;
  showRuntime: boolean;
}

const COPY: Record<ProjectKind, DialogCopy> = {
  saas: {
    eyebrow: 'SaaS Studio', title: 'Put your product in motion',
    blurb: 'Build a showcase, walkthrough or both with Kinetic. Bring screenshots and a brand kit; shape the story together.',
    types: ['Showcase + walkthrough', 'SaaS showcase', 'Product walkthrough'],
    ideaLabel: 'What should the viewer understand?',
    ideaPlaceholder: 'Introduce our dashboard, show how to create a project, then end with a sign-up invitation.',
    submit: 'Open SaaS Studio', allowCustomType: false, showRuntime: false
  },
  film: {
    eyebrow: 'New production',
    title: 'Start a new project',
    blurb: 'Format, runtime and budget lock the editor, shot stills and image-to-video. World character, asset and location sheets stay 1:1.',
    types: ['Short film', 'Film trailer', 'Brand film', 'Music video', 'Micro drama', 'Social video', 'Documentary', 'Animation'],
    ideaLabel: 'Project description (optional)',
    ideaPlaceholder: 'Add an overview, or bring your screenplay after creating the project.',
    submit: 'Create project →',
    allowCustomType: true,
    showRuntime: true
  },
  vfx: {
    eyebrow: 'RAW 2.0',
    title: 'Shape a new reality',
    blurb: 'Bring a plate and a direction. Your VFX Supervisor helps with references, tracked keep mattes, Seedance edits and final delivery.',
    types: ['Film VFX', 'Commercial VFX', 'Cleanup and replacement', 'Environment extension', 'Creature and character work'],
    ideaLabel: 'VFX scope',
    ideaPlaceholder:
      'Example: Replace the green screen with a storm environment while preserving the actor, costume edges, original timing and camera move.',
    submit: 'Open RAW 2.0',
    allowCustomType: false,
    showRuntime: false
  },
  marketing: {
    eyebrow: 'Marketing Studio',
    title: 'Start a marketing project',
    blurb: 'Tell us about the product. The crew ranks ad directions and storyboards before anything is generated.',
    types: ['Product ad', 'Social campaign', 'Performance creative', 'Brand film ad'],
    ideaLabel: 'What are you selling?',
    ideaPlaceholder: 'Describe the product, the audience and where the ad will run.',
    submit: 'Create marketing project',
    allowCustomType: false,
    showRuntime: false
  },
  upscale: {
    eyebrow: 'Upscale',
    title: 'Start an upscale job',
    blurb: 'Upload footage, pick a resolution, and confirm the spend before it runs.',
    types: ['Upscale'],
    ideaLabel: 'What are you upscaling?',
    ideaPlaceholder: 'Example: 12 social clips shot on a phone, deliver at 4K for broadcast.',
    submit: 'Create upscale job',
    allowCustomType: false,
    showRuntime: false
  }
};

const FORMATS = ['16:9', '2.39:1', '9:16', '4:3', '1:1'];

interface NewProjectDialogProps {
  kind: ProjectKind;
  onClose: () => void;
  onCreate: (input: NewProjectInput) => Promise<void>;
  onCreateSeries?: (input: NewSeriesInput) => Promise<void>;
  onCreateMarketing?: (input: Intake) => Promise<void>;
  businesses?: { id: string; name: string }[];
}

export const MICRODRAMA_TYPE = 'Micro drama';

export default function NewProjectDialog({ kind, onClose, onCreate, onCreateSeries, onCreateMarketing, businesses = [] }: NewProjectDialogProps) {
  const [manualMarketing, setManualMarketing] = useState(false);
  const copy = COPY[kind];
  const [selectedType, setSelectedType] = useState(copy.types[0]);
  const [startMode, setStartMode] = useState<SeriesStartMode>('idea');
  const microdrama = kind === 'film' && selectedType === MICRODRAMA_TYPE && Boolean(onCreateSeries);
  const [useCustomType, setUseCustomType] = useState(false);
  const [lookPreset, setLookPreset] = useState<string>(FILM_LOOK_PRESETS[0].id);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const [error, setError] = useState('');
  /* A screenplay brought to the dialog. Reading it is a paid call, so the price
     is fetched as soon as one is attached and shown on the button that commits
     to it — "create and analyse" is one decision, and a producer should see what
     it costs before making it, not in a dialog afterwards. */
  const [script, setScript] = useState('');
  const [scriptFilename, setScriptFilename] = useState('');
  const [quote, setQuote] = useState<{ inr: number; model: string } | null>(null);
  const [quoting, setQuoting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [brief, setBrief] = useState<BriefEstimateInput | null>(null);
  const [estimate, setEstimate] = useState<FilmEstimate | null>(null);
  const [estimatePackage, setEstimatePackage] = useState<TierKey | null>(null);
  const estimating = kind === 'film' && !microdrama;

  const readBrief = useCallback(() => {
    const form = formRef.current;
    if (!form || !estimating) { setBrief(null); return; }
    const data = new FormData(form);
    const type = String(data.get('type') || copy.types[0]);
    const runtimeMinutes = Number(data.get('runtime'));
    if (!(runtimeMinutes > 0)) { setBrief(null); return; }
    setBrief({
      runtimeMinutes,
      aspectRatio: String(data.get('format') || '16:9'),
      type: type === '__other__' ? String(data.get('customType') || 'Custom') : type,
      lookPreset,
      targetINR: Number(data.get('budgetInr')) || undefined
    });
  }, [estimating, lookPreset, copy.types]);

  useEffect(() => { readBrief(); }, [readBrief, selectedType]);

  useEffect(() => {
    const text = script.trim();
    if (!text) { setQuote(null); return; }
    let cancelled = false;
    setQuote(null);
    setQuoting(true);
    const timer = setTimeout(() => {
      api.screenplayQuote({ text, filename: scriptFilename || undefined })
        .then(result => { if (!cancelled) setQuote({ inr: result.inr, model: result.model }); })
        /* a quote that cannot be fetched must not block creating the project;
           the interpret route still asks for approval with a real number */
        .catch(() => { if (!cancelled) setQuote(null); })
        .finally(() => { if (!cancelled) setQuoting(false); });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); setQuoting(false); };
  }, [script, scriptFilename]);

  const chosenPackage: TierKey = estimatePackage || estimate?.recommendedPackage || 'star';
  const createLabel = estimating && estimate ? `Create · ${estimate.packages[chosenPackage].label} ≈ ${inrRange(estimate.packages[chosenPackage])}` : copy.submit;

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting.current) return;
    const form = new FormData(event.currentTarget);
    const selectedType = String(form.get('type') || copy.types[0]);
    if (kind === 'film' && !FILM_LOOK_PRESETS.some(preset => preset.id === lookPreset)) {
      setError('Pick a look before creating the project.');
      return;
    }
    submitting.current = true;
    setBusy(true);
    setError('');
    try {
      if (microdrama && onCreateSeries) {
        const text = (key: string) => String(form.get(key) || '').trim();
        await onCreateSeries({
          title: text('name') || 'Untitled series',
          format: text('format') || '9:16',
          startMode,
          idea: text('idea'),
          answers: { premise: text('idea'), episodes: text('episodes'), episodeLength: text('episodeLength'), language: text('language'), audience: text('audience') },
          ...(startMode !== 'idea' ? { source: text('source'), sourceName: startMode === 'story' ? 'Complete story' : 'Episode scripts' } : {}),
          lookPreset,
          budgetInr: Math.max(1000, Number(form.get('budgetInr')) || 25000),
        });
        return;
      }
      await onCreate({
        name: String(form.get('name') || 'Untitled project'),
        type: selectedType === '__other__' ? String(form.get('customType') || 'Custom') : selectedType,
        format: String(form.get('format') || '16:9'),
        runtimeMinutes: Number(form.get('runtime') || 5),
        idea: String(form.get('idea') || ''),
        fps: Number(form.get('fps') || 24),
        budgetInr: Math.max(1000, Number(form.get('budgetInr')) || 25000),
        ...(kind === 'film' ? { lookPreset } : {}),
        ...(script.trim() ? { script, scriptFilename: scriptFilename || 'Pasted screenplay.txt', ...(quote ? { scriptApprovedInr: Math.ceil(quote.inr) } : {}) } : {}),
        ...(estimating && estimate ? { estimatePackage: chosenPackage, estimateSnapshot: { at: new Date().toISOString(), stage: estimate.stage, package: chosenPackage, expectedINR: estimate.packages[chosenPackage].expectedINR, lowINR: estimate.packages[chosenPackage].lowINR, highINR: estimate.packages[chosenPackage].highINR, bandPct: estimate.bandPct, priceBasis: { capturedAt: estimate.priceBasis.capturedAt, live: estimate.priceBasis.live, fxINRPerUSD: estimate.priceBasis.fxINRPerUSD } } } : {})
      });
    } catch (cause) {
      submitting.current = false;
      setBusy(false);
      setError(cause instanceof Error ? cause.message : 'Could not create the project.');
    }
  };

  if (kind === 'marketing' && onCreateMarketing && !manualMarketing) {
    const startAgency = async (input: Intake) => {
      if (submitting.current) return;
      submitting.current = true;
      setBusy(true);
      setError('');
      try {
        await onCreateMarketing(input);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Cue could not start yet.');
      } finally {
        submitting.current = false;
        setBusy(false);
      }
    };
    return <div className="bs-theme"><Modal open onOpenChange={() => { if (!busy) onClose(); }} title="Cue · your marketing agency" description="Bring your business and what you want. Cue works out the rest and asks before anything is charged." size="lg" dismissible={!busy}>
      <MarketingIntake busy={busy} error={error} businesses={businesses} onSubmit={startAgency} onCancel={onClose} />
      <p className="bs-small bs-muted"><button type="button" className="agency-link" disabled={busy} onClick={() => setManualMarketing(true)}>Set up a campaign project manually</button> — for producers who want to choose formats and settings first.</p>
    </Modal></div>;
  }

  return <div className="bs-theme"><Modal open onOpenChange={() => { if (!busy) onClose(); }} title={copy.title} description={copy.blurb} size="lg" dismissible={!busy}>
    <form ref={formRef} onSubmit={submit} onChange={readBrief}>
      {error && <Alert tone="danger" title="Project could not be created">{error}</Alert>}
      <fieldset disabled={busy} className="studio-project-fields-group">
        <ProjectSetupFields series={{ active: microdrama, startMode, onStartMode: setStartMode, onType: setSelectedType }} kind={kind} copy={copy} formats={FORMATS} useCustomType={useCustomType} onCustomType={setUseCustomType} lookPreset={lookPreset} onLook={setLookPreset} script={script} scriptFilename={scriptFilename} onScript={(text, filename) => { setScript(text); if (filename !== undefined) setScriptFilename(filename); }} onError={setError} />
      </fieldset>
      {estimating && <EstimateCards input={brief} selected={estimatePackage} onSelect={setEstimatePackage} onEstimate={setEstimate} />}
      {estimating && estimate && <p className="bs-small bs-muted estimate-cards-note">Creating starts a {estimate.packages[chosenPackage].label} plan up to <span className="bs-mono">{inr(estimate.packages[chosenPackage].highINR)}</span>. Cue's writing and batches run inside it without stopping; past it, each click shows its own price.</p>}
      <div className="studio-project-setup-actions">
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button type="submit" variant="primary" loading={busy}>{microdrama ? (busy ? 'Creating series…' : 'Create series →') : busy ? (script.trim() ? 'Creating and reading…' : 'Creating…') : script.trim() ? `Create project & analyse${quote ? ` · ₹${Math.ceil(quote.inr)}` : quoting ? ' · pricing…' : ''}` : createLabel}</Button>
      </div>
    </form>
  </Modal></div>;
}
